import { ReactNode, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

import { getAccessiblePrimaryColor, getPortalCss, useUniversalLoader } from '@willow/shared-web';
import { PortalLoginBranding } from '@willow/types-iso';

import { Metadata } from '../components/metadata/Metadata';
import { hexToRgbA } from '../hooks/style-customization/utils';
import { AuthFeaturePanel } from './AuthFeaturePanel';

import './AuthLayout.scss';

const FAQ_URL = 'https://coda.io/@willow/willow-101';

interface Props {
  portal: PortalLoginBranding;
  // The browser tab title, which the Auth0 screens word differently from the heading.
  documentTitle: string;
  title: string;
  description?: ReactNode;
  faq?: 'login' | 'portal';
  children: ReactNode;
}

const FaqLine = ({ variant }: { variant: 'login' | 'portal' }) => {
  const link = (
    <a className="auth-layout__faq-link" href={FAQ_URL} target="_blank" rel="noreferrer">
      FAQ
    </a>
  );
  return (
    <div className="auth-layout__faq">
      {variant === 'login' ? (
        <>Trouble logging in? Visit our {link} page for help.</>
      ) : (
        <>Questions about your portal? Visit our {link} page.</>
      )}
    </div>
  );
};

export const AuthLayout = ({ portal, documentTitle, title, description, faq = 'portal', children }: Props) => {
  const loader = useUniversalLoader();

  const { name, logoUrl, primaryColor: configuredPrimaryColor } = portal;
  const primaryColor = getAccessiblePrimaryColor(configuredPrimaryColor);

  useEffect(() => {
    loader.setLoading(false);
  }, [loader]);

  return (
    <>
      <Metadata metadata={{ title: `${documentTitle} | ${name}` }} />
      <Helmet>{getPortalCss({ primaryColor, secondaryColor: hexToRgbA(primaryColor) })}</Helmet>

      <main id="main" className="main--full auth-layout">
        <div className="auth-layout__grid">
          <div className="auth-layout__form-column">
            <div className="auth-layout__form">
              {/* The card is mobile only. Below the two-column breakpoint the form sits on the
                  background photograph, which it is not readable against. The FAQ line stays
                  outside it, on the photo between the two cards, as the hosted page has it. */}
              <div className="auth-layout__form-card">
                <h1 className="auth-layout__title">{title}</h1>
                {description && <p className="auth-layout__description">{description}</p>}

                {children}
              </div>

              <FaqLine variant={faq} />
            </div>
          </div>

          <div className="auth-layout__feature-column">
            <div className="auth-layout__feature-inner">
              <AuthFeaturePanel portalName={name} logoUrl={logoUrl} />
            </div>
            <div className="auth-layout__bg" />
          </div>
        </div>
      </main>
    </>
  );
};
